import { waitDialog } from 'dialogs';
import { recordToEdit } from 'edit/recordToEdit';
import type { CreateTarget } from 'actions';
import { getConnection } from 'api/connections/endpoints/getConnection';
import type { Connection } from 'redux/slices/connectionsSlice';
import EditConnectionModal from '../EditConnectionModal';

export type ShowEditConnectionArgs = { readonly mode: 'create'; readonly createIn?: CreateTarget } | { readonly mode: 'edit'; readonly id: number };

/**
 * Open the connection create / edit modal and resolve when it closes (so the
 * caller can refresh). Standalone async: for edit it always fetches the record
 * by id (boundary-validated), so it works from the Workspace — which never
 * populates the list slice — and never opens the modal on a loosely-cast row.
 */
export const showEditConnection = async (args: ShowEditConnectionArgs): Promise<void> => {
  const createIn = args.mode === 'create' ? args.createIn : undefined;
  // Typed as the SLICE shape, not the wire one: what the modal accepts is `Connection`, and the projection below is
  // what turns one into the other.
  let connection: Connection | null = null;
  if (args.mode === 'edit') {
    const record = await recordToEdit('Connection', () => getConnection(args.id));
    if (record === undefined) return;
    // `worker_connections.priority` is a nullable COLUMN, but the slice's `Worker` declares it OPTIONAL — unset is
    // absence there, not a null. Mapping one to the other here keeps `FetchedConnection` assignable to the slice shape
    // the modal accepts, which is the contract `getConnection`'s docblock states. The modal reads `?.priority ?? 0`, so
    // an unset priority still edits as 0, exactly as it did when the declaration substituted one.
    connection = { ...record, workers: record.workers.map((w) => ({ ...w, priority: w.priority ?? undefined })) };
  }
  await waitDialog<void>((ok) => <EditConnectionModal show handleClose={ok} mode={args.mode} connection={connection} createIn={createIn} />);
};
